import { useCallback, useEffect, useMemo, useState } from 'react';
import { httpClient } from '@/shared/api/httpClient';
import { plataforma } from '@/platform';

// Paleta festiva para o degradê e os confetes do banner de aniversário.
const FESTIVO_CORES = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#eab308',
  '#22c55e',
  '#10b981',
  '#06b6d4',
  '#3b82f6',
  '#6366f1',
  '#a855f7',
  '#ec4899',
];

function embaralhar<T>(arr: readonly T[]): T[] {
  const copia = [...arr];
  for (let i = copia.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copia[i], copia[j]] = [copia[j], copia[i]];
  }
  return copia;
}

/**
 * Gera um degradê festivo ALEATÓRIO: ângulo qualquer + cores embaralhadas.
 * Repete a primeira cor no fim para o scroll do fundo fechar sem emenda.
 */
function gerarGradienteFestivo(): string {
  const angulo = Math.floor(Math.random() * 360);
  const cores = embaralhar(FESTIVO_CORES).slice(0, 6);
  const paradas = [...cores, cores[0]].join(', ');
  return `linear-gradient(${angulo}deg, ${paradas})`;
}

type Confete = {
  id: number;
  left: number;
  cor: string;
  tamanho: number;
  atraso: number;
  duracao: number;
  redondo: boolean;
};

function gerarConfetes(qtd: number): Confete[] {
  return Array.from({ length: qtd }, (_, id) => ({
    id,
    left: Math.random() * 100,
    cor: FESTIVO_CORES[Math.floor(Math.random() * FESTIVO_CORES.length)],
    tamanho: 5 + Math.random() * 6,
    atraso: Math.random() * 4,
    duracao: 2.6 + Math.random() * 2.8,
    redondo: Math.random() > 0.5,
  }));
}

/**
 * Banner de aviso definido pelo admin (servidor) para o usuário logado.
 * Busca em `/api/aviso` no mount, a cada 5 min (fallback) e na hora em que
 * o servidor empurra `aviso:atualizado` via WebSocket (admin criou/editou/
 * removeu). Estilo por tipo (info/aviso/urgente/aniversario). Fica até o
 * cliente fechar ou até `expiraEm`. O "fechar" é guardado localmente por
 * aviso (id+atualizadoEm), então editar a mensagem no painel faz o banner
 * reaparecer.
 */

type Aviso = {
  id: string;
  mensagem: string;
  tipo: 'info' | 'aviso' | 'urgente' | 'aniversario';
  expiraEm: string | null;
  atualizadoEm: string;
};

const CHAVE_DISPENSADO = 'orcamento.aviso.dispensado';
const INTERVALO_MS = 5 * 60 * 1000;

function chaveAviso(a: Aviso): string {
  return `${a.id}:${a.atualizadoEm}`;
}

export function BannerAviso() {
  const [aviso, setAviso] = useState<Aviso | null>(null);
  // Degradê festivo: aleatório no mount e trocado a cada 4s (só quando o banner
  // é de aniversário). Os confetes são gerados uma vez e ficam estáveis.
  const [gradiente, setGradiente] = useState<string>(() => gerarGradienteFestivo());
  const confetes = useMemo(() => gerarConfetes(30), []);
  const festivo = aviso?.tipo === 'aniversario';

  useEffect(() => {
    if (!festivo) return;
    setGradiente(gerarGradienteFestivo());
    const t = window.setInterval(() => setGradiente(gerarGradienteFestivo()), 4000);
    return () => window.clearInterval(t);
  }, [festivo]);

  const buscar = useCallback(async () => {
    try {
      const r = await httpClient.get<{ aviso: Aviso | null }>('/api/aviso');
      const a = r.aviso;
      if (!a) {
        setAviso(null);
        return;
      }
      // Expirou desde o último poll? Não mostra.
      if (a.expiraEm && new Date(a.expiraEm).getTime() <= Date.now()) {
        setAviso(null);
        return;
      }
      // Já foi dispensado nesta máquina (mesma versão do aviso)?
      if (localStorage.getItem(CHAVE_DISPENSADO) === chaveAviso(a)) {
        setAviso(null);
        return;
      }
      setAviso(a);
    } catch {
      // Silencioso — sem login/offline/erro: não mostra nada.
    }
  }, []);

  // Busca no mount + polling de 5 min como fallback.
  useEffect(() => {
    void buscar();
    const t = window.setInterval(() => void buscar(), INTERVALO_MS);
    return () => window.clearInterval(t);
  }, [buscar]);

  // Recarrega na hora quando o servidor empurra a mudança via WebSocket.
  useEffect(() => {
    const off = plataforma.aoMudarAviso(() => void buscar());
    return () => off();
  }, [buscar]);

  if (!aviso) return null;

  function fechar() {
    if (aviso) localStorage.setItem(CHAVE_DISPENSADO, chaveAviso(aviso));
    setAviso(null);
  }

  return (
    <div className={`banner-aviso banner-aviso--${aviso.tipo}`} role="status">
      {festivo && (
        <>
          <span
            className="banner-festivo-bg"
            aria-hidden="true"
            style={{ backgroundImage: gradiente }}
          />
          <span className="banner-confete" aria-hidden="true">
            {confetes.map((c) => (
              <span
                key={c.id}
                className="banner-confete__peca"
                style={{
                  left: `${c.left}%`,
                  width: c.tamanho,
                  height: c.tamanho,
                  background: c.cor,
                  borderRadius: c.redondo ? '50%' : '1px',
                  animationDelay: `${c.atraso}s`,
                  animationDuration: `${c.duracao}s`,
                }}
              />
            ))}
          </span>
        </>
      )}
      <span className="banner-aviso__msg">{aviso.mensagem}</span>
      <button
        type="button"
        className="banner-aviso__fechar"
        onClick={fechar}
        aria-label="Fechar aviso"
        data-tooltip-id="icon-tip"
      >
        ×
      </button>
    </div>
  );
}
